import React from 'react';
import { Box, Typography, Grid, Card, CardContent, CardActions, Button, Chip, alpha, useTheme } from '@mui/material';
import {
  PlayCircleOutline as PlayIcon,
  CheckCircle as CheckIcon,
  TimerOutlined,
  ReplayOutlined,
  SpaOutlined,
  FitnessCenterOutlined,
  InfoOutlined
} from '@mui/icons-material';
import paranayamaFallback from '../assets/images/paranayama.jpg';
import { useNavigate, useParams } from 'react-router';

const PranayamaList = ({ pranayamas, onStartTracking }) => {
  const theme = useTheme();
  

  if (!pranayamas?.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 10, px: 3 }}>
        <SpaOutlined sx={{ fontSize: 64, color: alpha(theme.palette.primary.main, 0.2), mb: 2 }} />
        <Typography variant="h6" color="text.primary" gutterBottom>
          No practices yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your pranayama sessions will appear here once added.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={{ xs: 2, sm: 3 }}>
      {pranayamas.map((pran) => {
        const isCompleted = pran.completed === 1;
        const difficulty = pran.difficulty_level?.toLowerCase() || '';
        const diffColor =
          difficulty === 'beginner' ? 'success' :
          difficulty === 'intermediate' ? 'warning' :
          difficulty === 'advanced' ? 'error' : 'default';

        return (
          <Grid item xs={12} sm={6} md={4} key={pran.id}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: isCompleted ? alpha(theme.palette.success.light, 0.08) : 'background.paper',
                border: isCompleted ? `1px solid ${theme.palette.success.main}` : undefined,
                position: 'relative'
              }}
            >
              {/* Compact hero */}
              <Box sx={{ position: 'relative', height: 160, overflow: 'hidden' }}>
                <img
                  src={pran.imageUrl || paranayamaFallback}
                  alt={pran.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 50%)'
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    left: 16,
                    right: 16,
                    color: 'white',
                    fontWeight: 700,
                    textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                    lineHeight: 1.3
                  }}
                >
                  {pran.name}
                </Typography>

                {/* Difficulty Chip */}
                {pran.difficulty_level && (
                  <Chip
                    label={pran.difficulty_level}
                    color={diffColor}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      fontWeight: 600,
                      color: 'white',
                      bgcolor: alpha(theme.palette[diffColor]?.dark || 'grey.700', 0.92),
                      backdropFilter: 'blur(6px)'
                    }}
                  />
                )}

                {/* NEW: Completed / Not Completed Badge */}
                <Chip
                  icon={isCompleted ? <CheckIcon fontSize="small" /> : null}
                  label={isCompleted ? 'Completed' : 'Not Completed'}
                  color={isCompleted ? 'success' : 'warning'}
                  size="small"
                  variant={isCompleted ? 'filled' : 'outlined'}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    fontWeight: 600,
                    bgcolor: isCompleted ? alpha(theme.palette.success.main, 0.9) : 'transparent',
                    color: isCompleted ? 'white' : theme.palette.warning.main,
                    borderColor: isCompleted ? 'transparent' : theme.palette.warning.main,
                    backdropFilter: 'blur(6px)',
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              </Box>

              <CardContent sx={{ flexGrow: 1, px: 2, py: 1.75 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Chip size="small" icon={<TimerOutlined fontSize="inherit" />} label={`${pran.duration}s`} />
                  <Chip size="small" icon={<ReplayOutlined fontSize="inherit" />} label={`${pran.loops}×`} />
                </Box>

                {pran.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, WebkitLineClamp: 2, overflow: 'hidden' }}>
                    {pran.description}
                  </Typography>
                )}

                {pran.instructions && (
                  <>
                    <Typography variant="caption" fontWeight={600}>
                      <InfoOutlined fontSize="inherit" /> Instructions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, WebkitLineClamp: 2, overflow: 'hidden' }}>
                      {pran.instructions}
                    </Typography>
                  </>
                )}

                {pran.breathing_sequence && (
                  <>
                    <Typography variant="caption" fontWeight={600}>
                      <SpaOutlined fontSize="inherit" /> Sequence
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {JSON.parse(pran.breathing_sequence)
                        .map((s) => `${s.type} ${s.seconds}s`)
                        .join(' • ')}
                    </Typography>
                  </>
                )}

                {pran.benefits && (
                  <>
                    <Typography variant="caption" fontWeight={600}>
                      <FitnessCenterOutlined fontSize="inherit" /> Benefits
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {JSON.parse(pran.benefits).slice(0, 2).join(' • ')}
                      {JSON.parse(pran.benefits).length > 2 && '…'}
                    </Typography>
                  </>
                )}
              </CardContent>

              <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <Button
            fullWidth
            variant={pran.completed === 1 ? 'outlined' : 'contained'}
            color={pran.completed === 1 ? 'success' : 'primary'}
            startIcon={pran.completed === 1 ? <CheckIcon /> : <PlayIcon />}
            // 2. Change this line to use the prop!
            onClick={() => onStartTracking(pran)} 
          >
            {pran.completed === 1 ? 'Revisit' : 'Start'}
          </Button>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PranayamaList;